import { confirmDiscardMassCommChangesNative } from '../manager/mass-comm-unsaved-guard.js';

export function pathOf(url) {
    return new URL(url, window.location.origin).pathname;
}

export function getQueryParams() {
    return new URLSearchParams(window.location.search);
}

export function readQueryParam(key) {
    return getQueryParams().get(key);
}

let knownPathname = typeof window !== 'undefined' ? window.location.pathname : '';

function buildUrl(path, params) {
    const pathname = path || window.location.pathname;
    const qs = params && params.toString ? params.toString() : '';
    return qs ? pathname + '?' + qs : pathname;
}

function rememberPathname(urlOrPath) {
    knownPathname = pathOf(urlOrPath || window.location.pathname);
}

export function pushUrl(path, params) {
    const next = buildUrl(path, params === undefined ? null : params);
    if (window.location.pathname + window.location.search === next) {
        rememberPathname(next);
        return;
    }
    window.history.pushState(null, '', next);
    rememberPathname(next);
}

export function replaceUrl(path, params) {
    const next = buildUrl(path, params === undefined ? getQueryParams() : params);
    if (window.location.pathname + window.location.search === next) {
        rememberPathname(next);
        return;
    }
    window.history.replaceState(null, '', next);
    rememberPathname(next);
}

export function writeQueryParams(patch, mode) {
    const params = getQueryParams();
    Object.keys(patch).forEach(function (key) {
        const value = patch[key];
        if (value == null || value === '') {
            params.delete(key);
        } else {
            params.set(key, String(value));
        }
    });
    const next = buildUrl(window.location.pathname, params);
    if (window.location.pathname + window.location.search === next) {
        return;
    }
    if (mode === 'push') {
        window.history.pushState(null, '', next);
        return;
    }
    window.history.replaceState(null, '', next);
}

export function createPathResolver(readPages) {
    return function pagePath(page, params) {
        const url = readPages()[page];
        if (!url) {
            throw new Error('pagePath: ' + page + ' is not configured');
        }
        return Object.entries(params || {}).reduce(function (path, entry) {
            return path.replace(':' + entry[0], encodeURIComponent(String(entry[1])));
        }, pathOf(url));
    };
}

function toPattern(path) {
    return new RegExp('^' + path.replace(/:\w+/g, '([^/]+)') + '/?$');
}

let handlingPopState = false;
let boundPopStateHandler = null;

export function isHandlingPopState() {
    return handlingPopState;
}

let navigationSequence = 0;
let settledNavigationSequence = 0;
const navigationInvalidationListeners = [];

export function onNavigationInvalidated(listener) {
    navigationInvalidationListeners.push(listener);
}

export function beginNavigation() {
    navigationSequence += 1;
    navigationInvalidationListeners.forEach(function (listener) {
        listener();
    });
    return navigationSequence;
}

export function getNavigationSequence() {
    return navigationSequence;
}

export function isStaleNavigation(token) {
    return token !== navigationSequence;
}

export function markNavigationSettled() {
    settledNavigationSequence = navigationSequence;
}

export function isNavigationSettled() {
    return settledNavigationSequence === navigationSequence;
}

export function setPageTitle(title) {
    document.title = title ? 'Shul Station | ' + title : 'Shul Station';
}

export function createRouter(pages, handlers, fallback, titles) {
    const routes = Object.entries(handlers)
        .filter(function (entry) {
            return Boolean(pages[entry[0]]);
        })
        .map(function (entry) {
            return {
                pattern: toPattern(pathOf(pages[entry[0]])),
                handler: entry[1],
                title: titles ? titles[entry[0]] : undefined,
            };
        });

    function loadCurrentPage() {
        const path = window.location.pathname;
        rememberPathname(path);
        const matched = routes
            .map(function (route) {
                return { route: route, found: route.pattern.exec(path) };
            })
            .find(function (result) {
                return result.found !== null;
            });

        if (!matched) {
            fallback();
            return;
        }

        setPageTitle(matched.route.title);
        matched.route.handler(...matched.found.slice(1).map(decodeURIComponent));
    }

    function onPopState() {
        const leftPathname = knownPathname;
        confirmDiscardMassCommChangesNative(function () {
            handlingPopState = true;
            try {
                loadCurrentPage();
            } finally {
                handlingPopState = false;
            }
        }, function () {
            window.history.pushState(null, '', leftPathname);
            knownPathname = leftPathname;
        });
    }

    if (boundPopStateHandler) {
        window.removeEventListener('popstate', boundPopStateHandler);
    }
    boundPopStateHandler = onPopState;
    window.addEventListener('popstate', boundPopStateHandler);

    return loadCurrentPage;
}
